import { Fullscreen, MoreVert } from '@mui/icons-material'
import ImageIcon from '@mui/icons-material/Image'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import SatelliteIcon from '@mui/icons-material/Satellite'
import VideoCameraBackIcon from '@mui/icons-material/VideoCameraBack'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import { blueGrey } from '@mui/material/colors'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import tileApi from 'apis/disApi/tileApi'
import LoadingSpinner from 'custom-components/LoadingSpinner'
import { IconThemeProvider } from 'custom-components/context/IconThemesContext'
import { useMemo, useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { checkAdminRole, isJson, logErrorMessage } from 'utils/functions/helpers'
import { tileKeys } from '../../hooks/useTileQuery'
import ImageViewerSettingsDialog from './ImageViewerSettingsDialog'

import ExpandedDialog from '../../../dis-v2/_components/ExpandedDialog'
import { useTileDashboard } from '../../dashboard/Dashboard'
import AudioScreen from './AudioScreen'
import ImageScreen from './ImageScreen'
import VideoScreen from './VideoScreen'
import { isEmpty, isEqual } from 'lodash'

import { off, onValue, ref } from 'firebase/database'
import database from '../../../../../firebase-config'
import { sleep } from 'utils/functions/helpers'

import { useViewReducer } from '../utils/tileViewDataQuery'
import { useRecordQuery } from '../utils/tileRecordQuery'

import {
	useImageBlobsRecordQueries,
	useImageViewerRecordQueries,
	useLoadBlobImageQueries,
} from './hooks/imageViewerRecordQuery'
import { getSelectedGridRecord } from '../../../../data/dataHelpers'

export default function ImageViewerTile({ tile, hideActionBtns, hiddenTiles }) {
	const { environment } = useSelector((state) => state)
	const iconTheme = environment.theme.icons
	const isAdmin = checkAdminRole(environment?.userCurrent)
	const { t } = useTranslation('common')
	const { id: dashboardKey } = useParams()

	const queryClient = useQueryClient()

	const { selectedFields } = useTileDashboard()

	const updateMutation = useMutation(tileApi.update, {
		onSuccess: () => queryClient.invalidateQueries(tileKeys.allWithKey(dashboardKey)),
	})

	const [settingTileDialogOpen, setSettingTileDialogOpen] = useState(false)
	const [expandedDialogOpen, setExpendedDialogOpen] = useState(false)
	// const [photos, setPhotos] = useState([])
	const [viewerPhotos, setViewerPhotos] = useState([])
	// const [videos, setVideos] = useState([])
	// const [audios, setAudios] = useState([])
	// const [locationImageList, setLocationImageList] = useState([])
	const [screenTab, setScreenTab] = useState(0) // 0: image, 1: video, 2: audio, 3: location
	const [isLoading, setIsLoading] = useState(false)

	const tileRef = useRef(null)
	const fetchIndex = useRef(0)

	const settings = useMemo(() => {
		if (tile?.settings && isJson(tile?.settings)) {
			return JSON.parse(tile?.settings ?? '{}')
		}

		return {}
	}, [tile?.settings])

	const isConnectedToHiddenDatagrid = useMemo(() => {
		if (isEmpty(hiddenTiles) || !settings?.connectedDatagridKey) return false

		return Boolean(hiddenTiles?.[settings?.connectedDatagridKey])
	}, [settings?.connectedDatagridKey, hiddenTiles])

	const selectedViewInHiddenDatagrid = useMemo(() => {
		if (!isConnectedToHiddenDatagrid) return null

		const datagridTile = hiddenTiles?.[settings?.connectedDatagridKey]
		if (!datagridTile) return null
		const parsedSettings = JSON.parse(datagridTile.settings)
		return parsedSettings?.viewKey || null
	}, [isConnectedToHiddenDatagrid, settings?.connectedDatagridKey, hiddenTiles])

	const { viewData, viewLoading } = useViewReducer({
		viewKey: selectedViewInHiddenDatagrid,
		tile,
		enabled: Boolean(selectedViewInHiddenDatagrid) && isConnectedToHiddenDatagrid,
	})

	const { records, recordsLoading, refetchRecords } = useRecordQuery({
		viewKey: viewData?.key,
		viewType: viewData?.type,
		clientFilter: { conditions: viewData?.queries?.[0]?.filter?.conditions } ?? {},
	})

	const [currentRecords, setCurrentRecords] = useState([])
	useEffect(() => {
		if (recordsLoading) return
		if (isEqual(records, currentRecords)) return
		setCurrentRecords(records)
	}, [recordsLoading, records])

	const currentRows = useMemo(() => {
		if (!currentRecords?.length || !viewData?.queries[0]?.fields?.length) return []

		return getSelectedGridRecord({
			records: currentRecords,
			columns: viewData?.queries[0]?.fields,
			environment,
			columnCheckList: viewData?.queries[0]?.fields,
			allColumns: viewData?.queries[0]?.fields,
		})
	}, [currentRecords, JSON.stringify(viewData?.queries[0]?.fields)])

	const [selectedRecordFromConnectedDatagrid, setSelectedRecordFromConnectedDatagrid] = useState([])

	const { data, isLoading: loadingBlobs } = useImageBlobsRecordQueries({
		records: selectedRecordFromConnectedDatagrid,
	})

	const recordWithSubmissionKey = useMemo(() => {
		return data.filter((rec) => Boolean(rec.submissionKey)) || []
	}, [JSON.stringify(data)])

	const { data: viewerPhotosData, isLoading: loadingImageViewer } = useImageViewerRecordQueries({
		records: loadingBlobs ? [] : recordWithSubmissionKey,
		selectConnectedGridKey: settings?.connectedDatagridKey,
	})

	const recordWithBlobs = useMemo(() => {
		if (isEmpty(recordWithSubmissionKey)) return null

		return recordWithSubmissionKey.filter((rec) => !isEmpty(rec.blobs))
	}, [JSON.stringify(recordWithSubmissionKey)])

	useEffect(() => {
		if (!settings?.connectedDatagridKey) {
			setSelectedRecordFromConnectedDatagrid([])
			return
		}

		if (isConnectedToHiddenDatagrid) {
			setSelectedRecordFromConnectedDatagrid(currentRows || [])
			return
		}

		const selectedField = selectedFields?.[settings?.connectedDatagridKey]
		if (selectedField) {
			setSelectedRecordFromConnectedDatagrid([selectedField])
			return
		}

		setSelectedRecordFromConnectedDatagrid([])
	}, [
		isConnectedToHiddenDatagrid,
		currentRows,
		settings?.connectedDatagridKey,
		JSON.stringify(selectedFields),
	])

	const {
		imageList: photos,
		videoList: videos,
		audioList: audios,
		locationList: locationImageList,
		isLoading: loadingImageData,
	} = useLoadBlobImageQueries({
		records: recordWithBlobs,
		isConnectedToHiddenDatagrid,
		selectConnectedGridKey: settings?.connectedDatagridKey,
	})

	useEffect(() => {
		if (!isConnectedToHiddenDatagrid) {
			setIsLoading(loadingBlobs || loadingImageData || loadingImageViewer)
		} else {
			setIsLoading(viewLoading || recordsLoading || loadingImageData)
		}
	}, [
		viewLoading,
		recordsLoading,
		loadingBlobs,
		loadingImageData,
		loadingImageViewer,
		isConnectedToHiddenDatagrid,
	])

	const photosLength = photos.length + viewerPhotos.length

	const displayViewerPhotosWithoutKeyRef = useRef([])

	useEffect(() => {
		if (isEmpty(viewerPhotosData)) {
			if (isEqual(displayViewerPhotosWithoutKeyRef.current, [])) return
			displayViewerPhotosWithoutKeyRef.current = []
			setViewerPhotos([])
			return
		}
		const viewerPhotosDataWithoutKeys = viewerPhotosData.map((item) => {
			const { key, ...rest } = item
			return rest
		})

		if (isEqual(displayViewerPhotosWithoutKeyRef.current, viewerPhotosDataWithoutKeys)) return

		displayViewerPhotosWithoutKeyRef.current = viewerPhotosDataWithoutKeys
		setViewerPhotos(viewerPhotosData)
	}, [viewerPhotosData, viewerPhotos])

	useEffect(() => {
		const customerKey = environment.account.key
		if (!isConnectedToHiddenDatagrid || !customerKey) return
		const customerCountRef = ref(database, `userSessions/customerKey/${customerKey}`)
		const viewRef = ref(database, `userSessions/viewActivity/customerKey/${customerKey}`)

		const customerUnsubscribe = onValue(customerCountRef, (snapshot) => {
			// const data = snapshot.val()
			// const currentDateTime = moment().add(serverOffset, 'milliseconds').format('MM/DD/YYYY HH:mm:ss')

			// if (data && areDatesEqual(data?.Updated, currentDateTime, 'MM/DD/YYYY HH:mm:ss')) {
			sleep(2000).then(() => {
				refetchRecords()
			})
			// }
		})

		const viewUnsubscribe = onValue(viewRef, (snapshot) => {
			sleep(2000).then(() => {
				refetchRecords()
			})
		})

		return () => {
			off(customerCountRef)
			off(viewRef)

			customerUnsubscribe()
			viewUnsubscribe()
		}
	}, [isConnectedToHiddenDatagrid, environment.account.key])

	const handleResizeTileWidth = async (width) => {
		try {
			const editedSettings = JSON.stringify({ ...settings, tileWidth: width })

			await updateMutation.mutateAsync({
				dashboardKey,
				tileKey: tile.key,
				data: { settings: editedSettings },
				token: environment.apiToken,
			})
		} catch (error) {
			logErrorMessage(error)
		}
	}

	const handleOpenSettingTileDialog = () => {
		setSettingTileDialogOpen(true)
	}

	const handleCloseSettingTileDialog = () => {
		setSettingTileDialogOpen(false)
	}

	const handleOpenExpandedDialog = () => {
		setExpendedDialogOpen(true)
	}

	const handleCloseExpandedDialog = () => {
		setExpendedDialogOpen(false)
	}

	const handleTabChange = (event, newValue) => {
		setScreenTab(newValue)
	}

	const handleSubmitSetting = async (data) => {
		try {
			const { imageType, connectedDatagridKey } = data

			await updateMutation.mutateAsync({
				dashboardKey,
				tileKey: tile.key,
				data: {
					settings: JSON.stringify({
						...settings,
						imageType,
						connectedDatagridKey,
					}),
				},
				token: environment.apiToken,
			})

			handleCloseSettingTileDialog()
		} catch (error) {
			logErrorMessage(error)
		}
	}

	return (
		<IconThemeProvider values={iconTheme}>
			<Box
				ref={tileRef}
				sx={{
					display: 'flex',
					flexDirection: 'column',
					width: '100%',
					height: '100%',
					boxShadow: 1,
					backgroundColor: blueGrey[50],
					position: 'relative',
					overflowX: 'hidden',
				}}
			>
				<Stack
					px={2}
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					sx={{
						position: 'sticky',
						top: 0,
						backgroundColor: '#eceff1',
						zIndex: 999,
					}}
				>
					<Stack direction="row" spacing={1}>
						<Typography fontWeight="bold">{tile?.i}</Typography>
						<Typography>
							{screenTab === 0 && photosLength > 0 ? `(${photosLength})` : null}
							{screenTab === 1 && videos.length > 0 ? `(${videos.length})` : null}
							{screenTab === 2 && audios.length > 0 ? `(${audios.length})` : null}
							{screenTab === 3 && locationImageList.length > 0
								? `(${locationImageList.length})`
								: null}
						</Typography>
					</Stack>
					<Tabs
						value={screenTab}
						onChange={handleTabChange}
						aria-label="tabs"
						sx={{
							'& .MuiButtonBase-root': {
								minHeight: '0 !important',
							},

							'& .MuiTabs-flexContainer': {
								flexWrap: 'wrap !important',
							},
						}}
					>
						<Tooltip title={t('common:input.image')}>
							<Tab icon={<ImageIcon />} aria-label={t('common:input.image')} />
						</Tooltip>
						<Tooltip title={t('common:input.video')}>
							<Tab icon={<VideoCameraBackIcon />} aria-label={t('common:input.video')} />
						</Tooltip>
						<Tooltip title={t('common:input.audio')}>
							<Tab icon={<RecordVoiceOverIcon />} aria-label={t('common:input.audio')} />
						</Tooltip>
						<Tooltip title={t('common:input.location')}>
							<Tab icon={<SatelliteIcon />} aria-label={t('common:input.location')} />
						</Tooltip>
					</Tabs>
					<Stack direction="row" alignItems="center" spacing={2}>
						{!hideActionBtns && (
							<>
								<Tooltip title={t('common:tooltip.expand')}>
									<IconButton aria-label="expand" onClick={handleOpenExpandedDialog}>
										<Fullscreen />
									</IconButton>
								</Tooltip>

								{isAdmin && (
									<IconButton
										aria-label="delete"
										size="small"
										onClick={handleOpenSettingTileDialog}
									>
										<MoreVert fontSize="small" />
									</IconButton>
								)}
							</>
						)}
					</Stack>
				</Stack>

				<Box sx={{ flex: 1, padding: 2, position: 'relative' }}>
					{isLoading && <LoadingSpinner />}

					{screenTab === 0 && (
						<ImageScreen photos={[...photos, ...viewerPhotos]} type={settings?.imageType} />
					)}
					{screenTab === 1 && <VideoScreen videos={videos} />}
					{screenTab === 2 && <AudioScreen audios={audios} />}
					{screenTab === 3 && (
						<ImageScreen photos={locationImageList} type={settings?.imageType} isLocation />
					)}
				</Box>

				<ImageViewerSettingsDialog
					tileElementWidth={tileRef?.current?.clientWidth}
					defaultTileWidth={settings.tileWidth}
					tile={tile}
					open={settingTileDialogOpen}
					settings={settings}
					isSubmitting={updateMutation.isLoading}
					showStable={hideActionBtns}
					onClose={handleCloseSettingTileDialog}
					onSubmit={handleSubmitSetting}
					onResizeTileWidth={handleResizeTileWidth}
				/>

				{expandedDialogOpen && (
					<ExpandedDialog open={expandedDialogOpen} onClose={handleCloseExpandedDialog}>
						<ImageViewerTile tile={tile} hideActionBtns />
					</ExpandedDialog>
				)}
			</Box>
		</IconThemeProvider>
	)
}
